import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/Button";
import { IntervalSelector } from "../../IntervalSelector";
import { addMonthsToDate } from "@/helpers/utils";
import {
  useChargeDate,
  useSelectedItemContext,
  updateSubscriptionQueryData,
  useUpdateBulkSubscriptions,
  useFetchSubscriptions,
} from "../../SelectedItemContext";
import { updateBulkOneTimeProduct } from "../../RechargeApiActions";
import { DrawerPageProps } from "../types";
import { Box } from "../../BoxSchedule";
import { ItemCard } from "../../SubProductCard";
import {
  droppsProductToProductCardProduct,
  getDisplayDate,
} from "../../helpers";

export interface DelayShipmentPageProps extends DrawerPageProps {
  shipment?: Box;
}

const DelayShipmentPage = ({
  notify,
  shipment,
  onExit,
}: DelayShipmentPageProps) => {
  const queryClient = useQueryClient();
  const { rechargeItem, product, variant } = useSelectedItemContext().boxItem;
  const nextChargeDate = rechargeItem.nextChargeDate;
  const nextChargeDateUpdator = useChargeDate();
  const bulkSubscriptionProcessor = useUpdateBulkSubscriptions();
  const fetchSubscriptionProcessor = useFetchSubscriptions();
  const [selectedShipDelay, setSelectedShipDelay] = useState(2);
  const [loading, setLoading] = useState<boolean>(false);
  const updatedDate = addMonthsToDate(nextChargeDate, selectedShipDelay);
  const productCardProduct = droppsProductToProductCardProduct({
    product,
    variant,
  });

  const handleShipDelayChange = (value: number | null) => {
    if (value) setSelectedShipDelay(value);
  };

  const handleShipDelaySubmit = () => {
    setLoading(true);
    if (!shipment || shipment?.items.length === 1) {
      //Only one item is there
      nextChargeDateUpdator.mutate(updatedDate, {
        onSuccess: () => {
          setLoading(false);
          notify({
            title: "Delayed Item",
            message: `Your item will now ship on ${getDisplayDate(
              updatedDate
            )}`,
          });
          onExit();
        },
        onError: () => {
          setLoading(false);
          notify({
            title: "Error",
            message: `An error occurred. Please try again.`,
            severity: "error",
          });
        },
      });
    } else {
      const { items, address } = shipment;
      const oneTimeIDs = items
        .filter((item) => item.rechargeItem.status === "ONETIME")
        .map((item) => item.rechargeItem.id);
      const subscriptionIds = items
        .filter((item) => item.rechargeItem.status !== "ONETIME")
        .map((item) => item.rechargeItem.id);
      const subscriptionData = subscriptionIds.map((id) => {
        return {
          subscriptionId: id,
          nextChargeDate: updatedDate,
        };
      });
      const onetimeData = oneTimeIDs.map((id) => {
        return {
          orderId: id,
          nextChargeDate: updatedDate,
        };
      });
      if (oneTimeIDs.length > 0 && subscriptionIds.length > 0) {
        //Mix od spot & sub
        bulkSubscriptionProcessor.mutate(
          {
            addressdId: address.id,
            subscriptions: subscriptionData,
          },
          {
            onSuccess(data) {
              updateBulkOneTimeProduct(onetimeData)
                .then(() => {
                  notify({
                    title: "Box Delayed",
                    message: `Your box will now ship on ${getDisplayDate(
                      updatedDate
                    )}`,
                  });
                  fetchSubscriptionProcessor.mutate();
                  onExit();
                })
                .catch((err) => {
                  notify({
                    title: "Error",
                    message: `An error occurred. Please try again.`,
                    severity: "error",
                  });
                });
            },
            onError: () => {
              notify({
                title: "Error",
                message: `An error occurred. Please try again.`,
                severity: "error",
              });
            },
          }
        );
      } else if (subscriptionIds.length === items.length) {
        //All sub
        bulkSubscriptionProcessor.mutate(
          {
            addressdId: address.id,
            subscriptions: subscriptionData,
          },
          {
            onSuccess(data) {
              notify({
                title: "Box Delayed",
                message: `Your box will now ship on ${getDisplayDate(
                  updatedDate
                )}`,
              });
              updateSubscriptionQueryData(queryClient, data);
              onExit();
            },
            onError: () => {
              notify({
                title: "Error",
                message: `An error occurred. Please try again.`,
                severity: "error",
              });
            },
          }
        );
      } else if (oneTimeIDs.length === items.length) {
        updateBulkOneTimeProduct(onetimeData)
          .then(() => {
            notify({
              title: "Box Delayed",
              message: `Your box will now ship on ${getDisplayDate(
                updatedDate
              )}`,
            });
            fetchSubscriptionProcessor.mutate();
            onExit();
          })
          .catch((err) => {
            notify({
              title: "Error",
              message: `An error occurred. Please try again.`,
              severity: "error",
            });
          });
      }
    }
  };

  return (
    <>
      <div className="w-full space-y-4">
        <h3 className="subtitle text-base font-semibold">
          {shipment && shipment.items.length > 1
            ? "Products to be Delayed"
            : "item to be Delayed"}
        </h3>
        <div className="space-y-4">
          {shipment ? (
            shipment.items.map((s) => {
              const { product, variant, rechargeItem } = s;
              const productCardProduct = droppsProductToProductCardProduct({
                product,
                variant,
              });
              return (
                <ItemCard
                  key={rechargeItem.id}
                  product={productCardProduct}
                  price={rechargeItem.price}
                  quantity={rechargeItem.quantity}
                  shippingInfo={rechargeItem.cadence ?? undefined}
                  className="rounded border border-solid border-primary"
                  isSubBadge={rechargeItem.status === "ONETIME" ? false : true}
                />
              );
            })
          ) : (
            <ItemCard
              key={product.id}
              product={productCardProduct}
              price={rechargeItem.price}
              quantity={rechargeItem.quantity}
              shippingInfo={rechargeItem.cadence ?? undefined}
              className="rounded border border-solid border-primary"
              isSubBadge={rechargeItem.status === "ONETIME" ? false : true}
            />
          )}
        </div>
      </div>
      <div className="w-full space-y-4">
        <IntervalSelector
          label={"Duration of Delay"}
          value={selectedShipDelay}
          onChange={handleShipDelayChange}
        />
        <p className="body text-sm">
          Current Ship Date: {getDisplayDate(nextChargeDate)}
        </p>
        <p className="body text-sm">
          Delaying by the duration above will move this ship date to:
        </p>
        <ul className="ml-5 text-sm">
          <li className="list-disc">{getDisplayDate(updatedDate)}</li>
        </ul>
        <Button
          className="w-full"
          onClick={handleShipDelaySubmit}
          loading={loading}
          data-portal-button="confirm-delay"
        >
          Confirm Delay
        </Button>
      </div>
    </>
  );
};

export { DelayShipmentPage };
